import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2, UserPlus } from 'lucide-react';
import api from '../../services/api';

const asList = (v) => (Array.isArray(v) ? v : []);

/**
 * Biblioteca de plantillas del coach (sin asignar).
 * Crear/editar abre el mismo editor de días que «Rutinas asignadas».
 */
export default function CoachRoutinesTab({
  onCreateTemplate,
  onEditTemplate,
  refreshKey = 0,
}) {
  const [routines, setRoutines] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [assignForId, setAssignForId] = useState(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [assignSearch, setAssignSearch] = useState('');

  const assignRoutine = useMemo(
    () => routines.find((r) => Number(r.id) === Number(assignForId)) || null,
    [routines, assignForId],
  );

  const assignClients = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const blob = `${c.nombre || ''} ${c.name || ''} ${c.email || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [clients, assignSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [routinesRes, clientsRes] = await Promise.all([
        api.get('/d28d/routines', { params: { scope: 'coach' } }),
        api.get('/training/coach/clients').catch(() => ({ data: { data: [] } })),
      ]);
      let list = asList(routinesRes.data?.data).filter((r) => {
        const scope = String(r.scope || 'coach_wl');
        return scope === 'coach_wl' || scope === '';
      });
      list = list.filter((r) => String(r.estado || 'activa') !== 'archivada');
      setRoutines(list);
      const clientList = asList(clientsRes.data?.data);
      setClients(clientList);
      setAssignUserId((prev) => prev || (clientList[0] ? String(clientList[0].id) : ''));
    } catch {
      setError('No se pudieron cargar tus rutinas.');
      setRoutines([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return routines;
    return routines.filter((r) =>
      String(r.nombre || '').toLowerCase().includes(q)
      || String(r.objetivo || '').toLowerCase().includes(q)
      || String(r.nivel || '').toLowerCase().includes(q)
      || String(r.id).includes(q),
    );
  }, [routines, search]);

  const openEdit = async (routineId) => {
    setBusyId(routineId);
    setError('');
    try {
      const resp = await api.get(`/training/coach/manual-routine/${routineId}/as-plan`);
      if (!resp.data?.success || !resp.data?.data) {
        throw new Error(resp.data?.error || 'No se pudo abrir la rutina');
      }
      onEditTemplate?.(resp.data.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error abriendo rutina');
    } finally {
      setBusyId(null);
    }
  };

  const archiveRoutine = async (routineId) => {
    if (!window.confirm('¿Archivar esta rutina de tu biblioteca? (no borra planes ya asignados)')) return;
    setBusyId(routineId);
    setError('');
    try {
      await api.post(`/d28d/routines/${routineId}/archive`);
      setSuccess('Rutina archivada.');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'No se pudo archivar');
    } finally {
      setBusyId(null);
    }
  };

  const openAssign = (routine) => {
    setAssignForId(routine.id);
    setAssignSearch('');
    setSuccess('');
    setError('');
    api.get('/training/coach/clients')
      .then((r) => {
        const list = asList(r.data?.data);
        setClients(list);
        setAssignUserId(list[0] ? String(list[0].id) : '');
      })
      .catch(() => {
        setAssignUserId('');
      });
  };

  const closeAssign = () => {
    if (busyId != null) return;
    setAssignForId(null);
    setAssignSearch('');
  };

  const assignToClient = async () => {
    if (!assignForId || !assignUserId) {
      setError('Elige un asesorado para asignar.');
      return;
    }
    setBusyId(assignForId);
    setError('');
    setSuccess('');
    try {
      const asPlan = await api.get(`/training/coach/manual-routine/${assignForId}/as-plan`).catch(() => null);
      const diasCount = Math.min(
        6,
        Math.max(2, asPlan?.data?.data?.dias?.length || 4),
      );
      await api.post('/training/coach/ai-assign-plan', {
        routine_id: Number(assignForId),
        user_id: Number(assignUserId),
        dias: diasCount,
      });
      setSuccess('Rutina asignada. Aparece en «Rutinas asignadas» y el cliente la ve en su entrenamiento.');
      setAssignForId(null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo asignar la rutina');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="atm-plans-toolbar">
        <div>
          <h3 className="text-lg font-bold text-stone-900 m-0">Rutinas</h3>
          <p className="text-sm text-stone-600 m-0 mt-1">
            Crea plantillas desde cero, guárdalas y asígnalas después a tus asesorados.
          </p>
        </div>
        <div className="atm-plans-toolbar__filters">
          <input
            type="search"
            className="input text-sm atm-plans-toolbar__search"
            placeholder="Buscar rutina…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="btn btn-primary text-sm" onClick={() => onCreateTemplate?.()}>
            + Nueva rutina
          </button>
        </div>
      </div>

      {error && assignForId == null ? <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm">{error}</div> : null}
      {success ? <div className="bg-lime-50 border border-lime-200 text-lime-900 p-3 rounded-xl text-sm">{success}</div> : null}

      {assignForId != null && typeof document !== 'undefined' ? createPortal(
        <div
          className="form-modal-overlay !z-[10000]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assign-routine-title"
          onClick={closeAssign}
        >
          <div
            className="form-modal d28d-admin-modal training-light-island surface-readable ui-light-panel bg-white border border-stone-200 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="assign-routine-title" className="text-lg font-bold text-stone-900 m-0">
              Asignar rutina
            </h3>
            <p className="text-sm text-stone-600 mt-1 mb-4">
              {assignRoutine?.nombre || `Rutina #${assignForId}`}
            </p>
            {error ? <div className="bg-red-50 text-red-700 p-3 rounded-xl text-sm mb-3">{error}</div> : null}
            {clients.length === 0 ? (
              <p className="text-sm text-stone-600 m-0 mb-4">
                No tienes asesorados todavía. Cuando un cliente esté vinculado a ti, aparecerá aquí.
              </p>
            ) : (
              <>
                <label className="block mb-2">
                  <span className="text-xs font-bold uppercase text-stone-500">Buscar asesorado</span>
                  <input
                    type="search"
                    className="input mt-1 w-full text-sm"
                    placeholder="Nombre o email…"
                    value={assignSearch}
                    onChange={(e) => setAssignSearch(e.target.value)}
                  />
                </label>
                <label className="block mb-4">
                  <span className="text-xs font-bold uppercase text-stone-500">Asignar a</span>
                  <select
                    className="input mt-1 w-full text-sm"
                    value={assignUserId}
                    onChange={(e) => setAssignUserId(e.target.value)}
                  >
                    <option value="">Seleccionar…</option>
                    {assignClients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nombre || c.name || c.email}
                        {c.email ? ` (${c.email})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary text-sm" onClick={closeAssign} disabled={busyId != null}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={busyId != null || !assignUserId || clients.length === 0}
                onClick={assignToClient}
              >
                {busyId != null ? 'Asignando…' : 'Confirmar asignación'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}

      {loading ? (
        <p className="text-stone-600">Cargando rutinas…</p>
      ) : (
        <div className="atm-plans-table-wrap">
          <table className="atm-plans-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Nivel</th>
                <th>Objetivo</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="atm-plans-table__id">{r.id}</td>
                  <td>
                    <span className="atm-plans-table__user">{r.nombre || `Rutina #${r.id}`}</span>
                    <span className="atm-plans-table__sub">{r.categoria || 'Manual'}</span>
                  </td>
                  <td>
                    <span className="atm-plans-badge">
                      {r.nivel || '—'}
                    </span>
                  </td>
                  <td className="atm-plans-table__muted">{r.objetivo || '—'}</td>
                  <td className="atm-plans-table__actions">
                    <div className="atm-plans-actions">
                      <button
                        type="button"
                        className="atm-plans-btn atm-plans-btn--edit"
                        disabled={busyId === r.id}
                        onClick={() => openEdit(r.id)}
                      >
                        <Pencil className="w-3.5 h-3.5" aria-hidden />
                        Editar
                      </button>
                      <button
                        type="button"
                        className="atm-plans-btn atm-plans-btn--assign"
                        disabled={busyId === r.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openAssign(r);
                        }}
                      >
                        <UserPlus className="w-3.5 h-3.5" aria-hidden />
                        Asignar
                      </button>
                      <button
                        type="button"
                        className="atm-plans-btn atm-plans-btn--danger"
                        disabled={busyId === r.id}
                        onClick={() => archiveRoutine(r.id)}
                        title="Archivar rutina"
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden />
                        Archivar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="atm-plans-table__empty">
                    Aún no tienes rutinas guardadas. Crea una con «Nueva rutina».
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function emptyCoachTemplatePlan() {
  return {
    id: 'tpl:new',
    is_template: true,
    routine_id: null,
    nombre: '',
    level: 'intermedio',
    method: 'hipertrofia',
    user_id: null,
    dias: [
      {
        dia: 1,
        nombre: 'Día 1',
        weekday: 'monday',
        completado: false,
        session_kind: 'gym',
        sport_prescription: null,
        warmup_url: '',
        stretching_url: '',
        technique_url: '',
        coach_notes: '',
        cardio: { goal: 'oxidación', bpm: 130, limit_type: 'time', limit_value: 20 },
        ejercicios: [
          {
            exercise_name: '',
            sets: 3,
            reps: '8-12',
            rest_seconds: 90,
            intensity_type: 'RPE',
            intensity_value: 8,
            notes: '',
            block_type: 'principal',
            youtube_url: '',
          },
        ],
      },
    ],
  };
}
